import { Check, X, Clock, Receipt } from 'lucide-react';
import { Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@employer-on-record/ui';
import { formatIDR, formatDateShort, type ReimbursementClaimDTO } from '@employer-on-record/shared';
import { Avatar } from '@/components/Avatar';
import { TONE_BY_REIMBURSEMENT_STATUS, LABEL_BY_REIMBURSEMENT_CATEGORY, LABEL_BY_REIMBURSEMENT_STATUS, cn } from '@/lib/utils';

interface Props {
	open: boolean;
	onClose: () => void;
	claim: ReimbursementClaimDTO | null;
}

export function ReimbursementDetailModal({ open, onClose, claim }: Props) {
	if (!claim) return null;
	const categoryLabel = LABEL_BY_REIMBURSEMENT_CATEGORY[claim.category as keyof typeof LABEL_BY_REIMBURSEMENT_CATEGORY] || claim.category;
	const tone = TONE_BY_REIMBURSEMENT_STATUS[claim.status] || 'gray';
	const sortedApprovals = [...(claim.approvals ?? [])].sort((a, b) => a.level - b.level);

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-[560px] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Reimbursement claim</DialogTitle>
					<DialogDescription>Submitted {formatDateShort(claim.createdAt)}</DialogDescription>
				</DialogHeader>

				<div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
					{claim.employee && (
						<div className="flex items-center gap-3 pb-4 border-b">
							<Avatar name={claim.employee.fullName} />
							<div>
								<div className="font-semibold text-[14px]">{claim.employee.fullName}</div>
								<div className="text-[11px] text-muted-foreground">{claim.employee.department || '—'}</div>
							</div>
						</div>
					)}

					<div className="grid grid-cols-2 gap-x-5 gap-y-3.5">
						<DetailField label="Category">
							<Badge tone="gray">{categoryLabel}</Badge>
						</DetailField>
						<DetailField label="Status">
							<Badge tone={tone}>{LABEL_BY_REIMBURSEMENT_STATUS[claim.status] ?? claim.status}</Badge>
						</DetailField>
						<DetailField label="Expense date">
							<div className="text-[13px] font-medium">{formatDateShort(claim.expenseDate)}</div>
						</DetailField>
						<DetailField label="Amount">
							<div className="text-[15px] font-semibold tabular">{formatIDR(Number(claim.amount))}</div>
						</DetailField>
						{claim.merchant && (
							<DetailField label="Merchant">
								<div className="text-[13px]">{claim.merchant}</div>
							</DetailField>
						)}
						{claim.sentToPaidAt && (
							<DetailField label="Sent to paid on">
								<div className="text-[13px]">{formatDateShort(claim.sentToPaidAt)}</div>
							</DetailField>
						)}
						{claim.reimbursedAt && (
							<DetailField label="Reimbursement paid on">
								<div className="text-[13px]">{formatDateShort(claim.reimbursedAt)}</div>
							</DetailField>
						)}
					</div>

					<DetailField label="Description">
						<div className="text-[13px] whitespace-pre-wrap bg-background p-3 rounded-md border">{claim.description}</div>
					</DetailField>

					{claim.receiptUrl && (
						<DetailField label="Receipt">
							<a href={claim.receiptUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[12px] text-primary hover:underline">
								<Receipt className="h-3 w-3" /> View receipt
							</a>
						</DetailField>
					)}

					{claim.status === 'rejected' && claim.rejectionReason && (
						<div className="border border-destructive/30 bg-red-50 rounded-md p-3.5">
							<div className="text-[11px] uppercase tracking-wider text-red-700 font-semibold mb-1">Rejection reason</div>
							<div className="text-[13px] text-red-800 whitespace-pre-wrap">{claim.rejectionReason}</div>
						</div>
					)}

					<div>
						<div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Approval timeline</div>
						{sortedApprovals.length === 0 ? (
							<div className="text-[12px] text-muted-foreground italic">No approvers configured.</div>
						) : (
							<ol className="relative space-y-2.5 pl-6">
								{sortedApprovals.map((step) => {
									const isCurrent = step.status === 'pending' && step.level === claim.currentLevel;
									const Icon = step.status === 'approved' ? Check : step.status === 'rejected' ? X : Clock;
									const dotClass =
										step.status === 'approved' ? 'bg-success text-white border-success' :
										step.status === 'rejected' ? 'bg-destructive text-white border-destructive' :
										isCurrent ? 'bg-warning text-white border-warning' :
										'bg-white text-muted-foreground border-border';
									return (
										<li key={step.id} className="relative">
											<span className={cn('absolute -left-6 top-0 h-5 w-5 rounded-full grid place-items-center border', dotClass)}>
												<Icon className="h-3 w-3" />
											</span>
											<div className="flex items-baseline justify-between gap-3">
												<div>
													<div className="text-[13px] font-medium">L{step.level} · {step.approverName ?? 'Unassigned'}</div>
													<div className="text-[11px] text-muted-foreground capitalize">{isCurrent ? 'Awaiting decision' : step.status}{step.decidedAt && ` · ${formatDateShort(step.decidedAt)}`}</div>
												</div>
											</div>
											{step.note && (
												<div className="mt-1 text-[12px] text-muted-foreground italic">"{step.note}"</div>
											)}
										</li>
									);
								})}
							</ol>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button variant="secondary" onClick={onClose}>Close</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div>
			<div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">{label}</div>
			{children}
		</div>
	);
}
